import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';

import './insuranceDetails.scss';
import { CustomAlert, Text } from '../../../shared/uiComponents';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import InsuranceHeader from './insuranceHeader';
import BillingRulesCard from './billingRulesCard';
import TelehealthCard from './telehealthCard';
import SubInsurancesCard from './subInsurancesCard';
import ClientsListCard from './clientsListCard';
import UsersListCard from './usersListCard';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	clearInsuranceDetails,
	getInsurance,
	getInsuranceClients,
	getInsuranceUsers,
} from '../../../redux/State/clientSlice/insuranceSlice';

interface InsuranceDetailModalProps {
	insuranceId: string;
	onClose: () => void;
	open: boolean;
}

const InsuranceDetailContent: FC<{
	insuranceId: string;
	onClose: () => void;
}> = ({ insuranceId }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const insurance = useSelector((state) => state.insurance.currentInsurance);
	const clients = useSelector((state) => state.insurance.clients);
	const users = useSelector((state) => state.insurance.users);
	const loading = useSelector((state) => state.insurance.loadingDetails);

	useEffect(() => {
		if (!insuranceId) return;

		dispatch(getInsurance(insuranceId)).catch(errorNormalizer);
		dispatch(getInsuranceClients(insuranceId)).catch(errorNormalizer);
		dispatch(getInsuranceUsers(insuranceId)).catch(errorNormalizer);

		// TODO - change to void
		// eslint-disable-next-line
		return () => {
			dispatch(clearInsuranceDetails());
		};
	}, [insuranceId, dispatch]);

	if (loading) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '300px',
					padding: '40px',
				}}
			>
				<CircularProgress size={40} />
			</div>
		);
	}

	if (!insurance) {
		return (
			<div style={{ padding: '20px' }}>
				<Text title="Insurance not found" type="psm" />
			</div>
		);
	}

	return (
		<div className="insuranceDetailWrapper" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
			<InsuranceHeader insurance={insurance} />

			<div className="insuranceDetailLayout">
				<div className="insuranceDetailColumn flexRatio3">
					<BillingRulesCard insurance={insurance} />
					<TelehealthCard insurance={insurance} />
					<SubInsurancesCard insurance={insurance} />
				</div>

				<div className="insuranceDetailColumn flexRatio2">
					<ClientsListCard clients={clients} />
					<UsersListCard users={users} />
					{/* <LocationsListCard locations={locations} /> */}
				</div>
			</div>
		</div>
	);
};

const InsuranceDetailModal: FC<InsuranceDetailModalProps> = ({ insuranceId, open, onClose }) => (
	<CustomAlert
		title="Insurance Details"
		open={open}
		onClose={onClose}
		Content={() => <InsuranceDetailContent insuranceId={insuranceId} onClose={onClose} />}
		maxWidth="lg"
		noPadding={false}
	/>
);

export default InsuranceDetailModal;
